import { StyleSheet, ViewProps } from 'react-native'
import {
  CalendarValue,
  Spacing,
  displayTitlesForCalendarValue,
  calendarValueToInsightsFromTo,
  Weekday,
  titleForWeekday,
} from 'zens-storybook'
import { InsightTimeType, useBuilding, useCompany, useInsightsTime, useStore, useWorkspace } from 'zens-store'
import { useTranslation } from 'react-i18next'
import generatePDF from 'react-to-pdf'
import * as XLSX from 'xlsx'
import {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  ReactElement,
  RefAttributes,
  useImperativeHandle,
} from 'react'

import { differenceInCalendarDays } from 'date-fns'

import { groupByDay, groupByWeek } from '@dms/helper/insights'

import InsightsTimeGraph from './timegraph'
import { GraphBar } from './graphbarselect'

const styles = StyleSheet.create({
  container: {
    marginTop: Spacing.M,
  },
})

export interface InsightsTimeRef {
  download: (key: string) => Promise<void>
}

interface Props extends ViewProps {
  companyId: number
  floorId?: number
  period: CalendarValue
  weekdays?: Weekday[]
  workingHours?: boolean
  type?: InsightTimeType
  onChangeType?: (type: InsightTimeType) => void
  buildingId: number
  workspaceId?: number
  onChangeWorkspaceId?: (workspaceId: number) => void
  bars?: GraphBar[]
  onChangeBars?: (bars?: GraphBar[]) => void
}

const InsightsTime = forwardRef<InsightsTimeRef, Props>((props, ref) => {
  const {
    companyId,
    floorId,
    weekdays,
    workingHours,
    period,
    type = 'day',
    onChangeType,
    buildingId,
    workspaceId = 0,
    onChangeWorkspaceId,
    bars,
    onChangeBars,
    style,
    ...otherProps
  } = props
  const { i18n, message } = useStore()
  const { t } = useTranslation()

  const company = useCompany({ companyId })
  const building = useBuilding({ companyId, buildingId })
  const workspace = useWorkspace({ companyId, workspaceId })

  const dates = calendarValueToInsightsFromTo(period, weekdays)
  const rangeInDays = dates ? differenceInCalendarDays(dates.to!, dates.from!) + 1 : 0
  const enabled = !!dates && rangeInDays <= 366

  const insights = useInsightsTime(
    {
      companyId,
      buildingId,
      floorId,
      from: dates?.from || new Date(),
      to: dates?.to || new Date(),
      weekdays,
      workingHours,
      timeType: type,
      workspaceId,
    },
    {
      enabled: enabled && !!companyId && (!!buildingId || !!floorId),
    },
  )

  const items =
    type === 'day'
      ? !weekdays || weekdays.length > 1
        ? groupByWeek(insights.data)
        : insights.data
      : groupByDay(insights.data)

  useImperativeHandle(ref, () => ({
    download: (key) => download(key as 'data' | 'graph'),
  }))

  async function download(key: 'data' | 'graph') {
    if (key === 'data') {
      try {
        const periodTitles = displayTitlesForCalendarValue(period, i18n.language)
        const rows: (string | number | Date)[][] = []
        rows.push([t('Company'), company.data?.name || '-'])
        rows.push([t('Building'), building.data?.name || '-'])
        if (workspaceId) rows.push([t('Workspace'), workspace.data?.name || '-'])
        rows.push([t('Type'), type === 'day' ? t('Daily') : t('Hourly')])
        rows.push([t('From'), periodTitles[0] || '-'])
        rows.push([t('To'), periodTitles[1] || '-'])
        rows.push([t('Weekdays'), ...(weekdays?.map((weekday) => titleForWeekday(weekday, t)) || [t('All days')])])
        rows.push([t('Working hours'), workingHours ? t('Yes') : t('No')])
        rows.push([])
        rows.push([t('Date'), ...(!bars || bars.includes('average') ? [t('Average occupancy')] : [])])
        insights.data?.forEach((item) =>
          rows.push([item.date, ...((!bars || bars.includes('average')) && item.value !== null ? [item.value] : [])]),
        )

        const worksheet = XLSX.utils.aoa_to_sheet(rows)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Insights')
        XLSX.writeFile(workbook, `${t('Average occupancy over time')}.xlsx`, { compression: true })
      } catch (error) {
        message.add({ message: t('Error generating data'), status: 'error' })
        console.error('Error generating data', error)
      }
    } else {
      try {
        await generatePDF(() => document.getElementById('InsightsTimeGraph'), {
          filename: `${t('Average occupancy over time')}.pdf`,
        })
      } catch (error) {
        message.add({ message: t('Error generating graph'), status: 'error' })
        console.error('Error generating graph', error)
      }
    }
  }
  return (
    <InsightsTimeGraph
      insights={items}
      companyId={companyId}
      buildingId={buildingId}
      floorId={floorId}
      type={type}
      onChangeType={onChangeType}
      workspaceId={workspaceId}
      onChangeWorkspaceId={onChangeWorkspaceId}
      bars={bars}
      onChangeBars={onChangeBars}
      period={period}
      style={[styles.container, style]}
      {...otherProps}
    />
  )
}) as ForwardRefExoticComponent<PropsWithoutRef<Props> & RefAttributes<InsightsTimeRef>> & {
  Skeleton: () => ReactElement
}

InsightsTime.Skeleton = () => <InsightsTimeGraph.Skeleton style={styles.container} />

export default InsightsTime
